import { useState, useEffect } from 'react';
import type { FC } from 'react';
import type { Truck, Driver } from '../types';
import './TruckDetails.css';

interface TruckDetailsProps {
  truck: Truck;
  route: { latitude: number; longitude: number; timestamp?: string }[];
  drivers: Driver[];
  onClose: () => void;
}

const TruckDetails: FC<TruckDetailsProps> = ({ truck, route, drivers, onClose }) => {
  const [address, setAddress] = useState<string | null>(null);
  const [loadingAddress, setLoadingAddress] = useState(false);
  
  // Find ALL drivers assigned to this truck
  const assignedDrivers = drivers.filter(d => d.assignedTruck === truck.id);

  // Fetch address from coordinates using reverse geocoding
  useEffect(() => {
    const fetchAddress = async () => {
      setLoadingAddress(true);
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${truck.latitude}&lon=${truck.longitude}&zoom=18&addressdetails=1`,
          {
            headers: {
              'Accept-Language': 'sv',
            },
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          if (data.address) {
            // Build a readable address
            const parts = [];
            if (data.address.road) parts.push(data.address.road);
            if (data.address.house_number) parts[0] = `${data.address.road} ${data.address.house_number}`;
            if (data.address.city || data.address.town || data.address.village) {
              parts.push(data.address.city || data.address.town || data.address.village);
            }
            if (data.address.postcode) parts.push(data.address.postcode);
            
            setAddress(parts.join(', ') || data.display_name?.split(',').slice(0, 3).join(',') || null);
          }
        }
      } catch (err) {
        console.error('Failed to fetch address:', err);
        setAddress(null);
      } finally {
        setLoadingAddress(false);
      }
    };

    fetchAddress();
  }, [truck.latitude, truck.longitude]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('sv-SE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const calculateDistance = () => {
    if (route.length < 2) return 0;
    
    let totalDistance = 0;
    for (let i = 1; i < route.length; i++) {
      const lat1 = route[i - 1].latitude * Math.PI / 180;
      const lat2 = route[i].latitude * Math.PI / 180;
      const dLat = lat2 - lat1;
      const dLng = (route[i].longitude - route[i - 1].longitude) * Math.PI / 180;
      
      const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      totalDistance += 6371 * c; // Earth's radius in km
    }
    return totalDistance;
  };

  const getStatusInfo = () => {
    switch (truck.status) {
      case 'moving':
        return { text: 'I rörelse', color: 'var(--color-success)', icon: '🚛' };
      case 'idle':
        return { text: 'Stillastående', color: 'var(--color-warning)', icon: '⏸️' };
      default:
        return { text: 'Offline', color: 'var(--color-danger)', icon: '⚠️' };
    }
  };

  const statusInfo = getStatusInfo();
  const distance = calculateDistance();

  return (
    <div className="truck-details">
      <div className="details-header">
        <div className="details-title">
          <div className="truck-avatar">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="1" y="3" width="15" height="13" rx="2"/>
              <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
              <circle cx="5.5" cy="18.5" r="2.5"/>
              <circle cx="18.5" cy="18.5" r="2.5"/>
            </svg>
          </div>
          <div>
            <h2>{truck.name}</h2>
            {truck.plateNumber && (
              <span className="plate-number font-mono">{truck.plateNumber}</span>
            )}
          </div>
        </div>
        <button className="close-btn" onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      <div className="status-banner" style={{ background: `${statusInfo.color}20`, borderColor: statusInfo.color }}>
        <span className="status-emoji">{statusInfo.icon}</span>
        <span className="status-text" style={{ color: statusInfo.color }}>{statusInfo.text}</span>
        <span className="status-speed">{Math.round(truck.speed)} km/h</span>
      </div>

      <div className="details-content">
        <div className="stats-grid">
          <div className="stat-card stat-card-wide position-card">
            <div className="stat-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="10" r="3"/>
                <path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 1 0-16 0c0 3 2.7 6.9 8 11.7z"/>
              </svg>
            </div>
            <div className="stat-info">
              <span className="stat-label">Position</span>
              {loadingAddress ? (
                <span className="stat-value address-loading">Hämtar adress...</span>
              ) : address ? (
                <span className="stat-value address-text">{address}</span>
              ) : null}
              <div className="coordinates-row">
                <span className="coordinate-item">
                  <span className="coord-label">LAT</span>
                  <span className="coord-value font-mono">{truck.latitude.toFixed(6)}</span>
                </span>
                <span className="coordinate-item">
                  <span className="coord-label">LNG</span>
                  <span className="coord-value font-mono">{truck.longitude.toFixed(6)}</span>
                </span>
              </div>
            </div>
          </div>

          {/* Assigned Drivers Card */}
          <div className={`stat-card stat-card-wide driver-card ${assignedDrivers.length > 0 ? 'has-driver' : ''}`}>
            <div className="stat-icon driver-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
            </div>
            <div className="stat-info">
              <span className="stat-label">
                {assignedDrivers.length > 1 
                  ? `Tilldelade förare (${assignedDrivers.length})` 
                  : 'Tilldelad förare'}
              </span>
              {assignedDrivers.length > 0 ? (
                <div className="drivers-list">
                  {assignedDrivers.map((driver) => (
                    <div key={driver.id} className="driver-details">
                      <span className="stat-value driver-name">{driver.name}</span>
                      <div className="driver-meta">
                        <span className={`driver-status ${driver.isTracking ? 'tracking' : 'inactive'}`}>
                          <span className="status-dot"></span>
                          {driver.isTracking ? 'Spårar' : 'Inaktiv'}
                        </span>
                        {driver.batteryLevel !== null && driver.batteryLevel !== undefined && (
                          <span className="driver-battery">
                            🔋 {driver.batteryLevel}%
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="stat-value no-driver">Ingen förare tilldelad</span>
              )}
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
              </svg>
            </div>
            <div className="stat-info">
              <span className="stat-label">Hastighet</span>
              <span className="stat-value">{Math.round(truck.speed)} km/h</span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2L4.5 20.3l7.5-4.5 7.5 4.5L12 2z"/>
              </svg>
            </div>
            <div className="stat-info">
              <span className="stat-label">Riktning</span>
              <span className="stat-value">{Math.round(truck.heading)}°</span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21.54 15.03a9.97 9.97 0 0 1-6.88 6.88M3.34 11.27a10 10 0 0 1 7.93-8.17"/>
                <circle cx="12" cy="12" r="2"/>
              </svg>
            </div>
            <div className="stat-info">
              <span className="stat-label">Satelliter</span>
              <span className="stat-value">{truck.satellites || '-'}</span>
            </div>
          </div>

          {truck.altitude !== undefined && (
            <div className="stat-card">
              <div className="stat-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="m8 3 4 8 5-5 5 15H2L8 3z"/>
                </svg>
              </div>
              <div className="stat-info">
                <span className="stat-label">Höjd</span>
                <span className="stat-value">{Math.round(truck.altitude)} m</span>
              </div>
            </div>
          )}

          <div className="stat-card">
            <div className="stat-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
              </svg>
            </div>
            <div className="stat-info">
              <span className="stat-label">Körd sträcka</span>
              <span className="stat-value">{distance.toFixed(2)} km</span>
            </div>
          </div>
        </div>

        <div className="timeline-section">
          <h3>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
            Senaste uppdatering
          </h3>
          <div className="last-update-info">
            <span className="update-time">{formatDate(truck.lastUpdate)}</span>
            {truck.deviceTimestamp && truck.deviceTimestamp !== truck.lastUpdate && (
              <span className="device-time text-muted">
                Enhetstid: {formatDate(truck.deviceTimestamp)}
              </span>
            )}
          </div>
        </div>

        <div className="route-info">
          <h3>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="6" cy="19" r="3"/>
              <path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15"/>
              <circle cx="18" cy="5" r="3"/>
            </svg>
            Rutt
          </h3>
          <p className="route-points">{route.length} punkter registrerade</p>
        </div>
      </div>

      <div className="details-footer">
        <button className="btn btn-secondary" onClick={onClose}>
          Stäng
        </button>
        <button className="btn btn-primary" onClick={() => {
          const url = `https://www.google.com/maps/search/?api=1&query=${truck.latitude},${truck.longitude}`;
          window.open(url, '_blank');
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
            <polyline points="15 3 21 3 21 9"/>
            <line x1="10" y1="14" x2="21" y2="3"/>
          </svg>
          Öppna i Maps
        </button>
      </div>
    </div>
  );
};

export default TruckDetails;
